// ─────────────────────────────────────────
//  SETTINGS & SUBSCRIPTION
// ─────────────────────────────────────────

import { S, sb, ls, setLs, rmLs } from './state.js';
import { showToast } from './utils.js';

export function openSettings() {
  // Name comes from state (loaded during auth) or localStorage fallback
  const name = S.name || ls('vc_name') || '';
  const key = S.key || ls('vc_key') || '';
  const email = S.session?.user?.email || '';

  const nameEl = document.getElementById('set-name');
  const keyEl = document.getElementById('set-key');
  if (nameEl) nameEl.value = name;
  if (keyEl) keyEl.value = key;

  // Show email (read-only)
  const emailEl = document.getElementById('set-email');
  if (emailEl) emailEl.value = email;

  // Show current plan info
  const planInfo = document.getElementById('set-plan-info');
  if (planInfo) {
    planInfo.textContent = S.isAdmin ? 'Admin (unlimited)' : S.tier.toUpperCase();
  }

  // Show builds used
  const buildsInfo = document.getElementById('set-builds-info');
  if (buildsInfo) {
    const limits = { free: 5, member: 20, pro: 99999, pro_annual: 99999, pro_builder: 99999, cohort: 50, byok: 99999, starter: 25, pro_plus: 99999, annual: 99999, builder: 99999 };
    const limit = limits[S.tier] ?? 5;
    if (limit >= 99999) {
      buildsInfo.textContent = `${S.builds} builds used (unlimited plan)`;
    } else {
      const remaining = Math.max(0, limit - S.builds);
      buildsInfo.textContent = `${remaining} builds remaining this month`;
    }
  }

  document.getElementById('settings-modal')?.classList.add('open');

  // Update connected accounts status
  updateAccountStatus();
}

export function closeSettings(e) {
  const modal = document.getElementById('settings-modal');
  if (!e || e.target === modal) {
    modal?.classList.remove('open');
  }
}

// Exit playground — clears current session state but keeps account signed in
export function exitPlayground() {
  closeSettings();
  S.history = [];
  S.code = '';
  S.convId = null;
  S.projectId = null;
  S.localProjectId = null;

  // Clear editor and preview
  const editor = document.getElementById('code-editor');
  if (editor) editor.value = '';
  const iframe = document.getElementById('preview-iframe');
  if (iframe) iframe.srcdoc = '';
  const url = document.getElementById('preview-url');
  if (url) url.textContent = 'No preview yet — describe something and hit Run';

  // Show auth screen (user can sign back in instantly since session persists)
  document.getElementById('auth-screen')?.classList.remove('hidden');
  const userPill = document.getElementById('user-pill');
  if (userPill) userPill.style.display = 'none';

  showToast('Exited playground');
}

export function saveSettings() {
  const n = document.getElementById('set-name')?.value.trim() || '';
  const k = document.getElementById('set-key')?.value.trim() || '';

  // Save API key
  if (k !== S.key) {
    S.key = k;
    if (k) { setLs('vc_key', k); } else { rmLs('vc_key'); }
  }

  // Save name (persist locally so it survives reload)
  if (n) {
    S.name = n;
    setLs('vc_name', n);
    const pillName = document.getElementById('user-pill-name');
    if (pillName) pillName.textContent = n;
    const tgGreet = document.getElementById('tg-greeting');
    if (tgGreet) tgGreet.textContent = `Hey ${n}! What will you build today?`;
    // Sync to Supabase in background (fire and forget)
    if (S.session) {
      sb.from('users').update({ name: n }).eq('id', S.session.user.id)
        .then(() => {})
        .catch(() => {});
    }
  }

  document.getElementById('settings-modal')?.classList.remove('open');
  showToast('✓ Settings saved');
}

export function showSub() {
  const titleEl = document.getElementById('sub-title-text');
  const descEl = document.getElementById('sub-desc-text');

  const hasPaidPlan = ['pro', 'pro_annual', 'pro_plus', 'annual', 'pro_builder'].includes(S.tier);

  if (hasPaidPlan) {
    if (titleEl) titleEl.textContent = "You've used all your builds this month";
    if (descEl) descEl.textContent = 'Buy more credits to keep building.';
  } else if (S.tier === 'member') {
    if (titleEl) titleEl.textContent = "You've used your included builds";
    if (descEl) descEl.textContent = 'Academy members get 20 builds/month. Buy credits for more.';
  } else {
    if (titleEl) titleEl.textContent = "You've used your free builds";
    if (descEl) descEl.textContent = 'Buy credits to keep building. No API key needed.';
  }

  const gate = document.getElementById('sub-gate');
  if (gate) gate.classList.add('show');
}

export function subscribePlan(plan) {
  if (!S.session) { showToast('Please sign in first'); return; }

  _pendingPlan = plan;
  setProcessingPlan(plan, true);

  fetch('/api/yoco/checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + S.session.access_token,
    },
    body: JSON.stringify({ planId: plan }),
  })
    .then(res => res.json())
    .then(data => {
      if (data.redirectUrl && data.checkoutId) {
        sessionStorage.setItem('yoco_checkout_id', data.checkoutId);
        sessionStorage.setItem('yoco_plan_id', plan);
        window.location.href = data.redirectUrl;
      } else {
        showToast('⚠ Could not start payment: ' + (data.error || 'Unknown error'));
        setProcessingPlan(plan, false);
      }
    })
    .catch(() => {
      showToast('⚠ Payment failed — check your connection');
      setProcessingPlan(plan, false);
    });
}

export function closeProPlus(e) {
  const modal = document.getElementById('pro-plus-modal');
  if (!e || e.target === modal) {
    modal?.classList.remove('open');
  }
}

export function subscribeProPlus() {
  document.getElementById('pro-plus-modal')?.classList.remove('open');
  subscribePlan('pro_plus');
}

export function subscribeProPlusAnnual() {
  document.getElementById('pro-plus-modal')?.classList.remove('open');
  subscribePlan('pro_plus_annual');
}

// ── Internal helpers ──
let _pendingPlan = null;

function setProcessingPlan(plan, processing) {
  const cards = document.querySelectorAll('.plan-card');
  cards.forEach(card => {
    if (processing) {
      card.style.opacity = '0.5';
      card.style.pointerEvents = 'none';
    } else {
      card.style.opacity = '1';
      card.style.pointerEvents = 'auto';
    }
  });
}

export function backToPlans() {
  const step = document.getElementById('sub-payment-step');
  const confirm = document.getElementById('sub-confirm-step');
  if (step) step.style.display = 'block';
  if (confirm) confirm.style.display = 'none';
  _pendingPlan = null;
}

export function closeSub() {
  const gate = document.getElementById('sub-gate');
  if (gate) gate.classList.remove('show');
  const step = document.getElementById('sub-payment-step');
  const confirm = document.getElementById('sub-confirm-step');
  if (step) step.style.display = 'block';
  if (confirm) confirm.style.display = 'none';
  _pendingPlan = null;
}

export function redeemPromo() {
  const input = document.getElementById('promo-input');
  const code = input?.value.trim();

  if (!code) { showPromoMsg('Enter a promo code', 'var(--red)'); return; }
  if (!S.session) { showPromoMsg('Please sign in first', 'var(--red)'); return; }

  showPromoMsg('Applying...', 'var(--muted)');

  fetch('/api/promo/redeem', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + S.session.access_token,
    },
    body: JSON.stringify({ code }),
  })
    .then(res => res.json().then(data => ({ ok: res.ok, data })))
    .then(({ ok, data }) => {
      if (!ok) {
        showPromoMsg(data.error || 'Invalid code', 'var(--red)');
        return;
      }

      S.tier = data.tier;
      if (S.isAdmin) S.tier = 'annual';
      const tierEl = document.getElementById('user-pill-tier');
      if (tierEl) tierEl.textContent = S.isAdmin ? 'ADMIN' : S.tier.toUpperCase();

      showPromoMsg(data.message, 'var(--green)');
      showToast('✓ Promo code applied!');

      setTimeout(() => {
        const gate = document.getElementById('sub-gate');
        if (gate) gate.classList.remove('show');
        if (input) input.value = '';
        const msgEl = document.getElementById('promo-msg');
        if (msgEl) msgEl.style.display = 'none';
      }, 2000);
    })
    .catch(() => {
      showPromoMsg('Connection error — try again', 'var(--red)');
    });
}

function showPromoMsg(text, color) {
  const el = document.getElementById('promo-msg');
  if (!el) return;
  el.textContent = text;
  el.style.color = color;
  el.style.display = 'block';
}

// ─────────────────────────────────────────
//  CONNECTED ACCOUNTS (GitHub + Supabase)
// ─────────────────────────────────────────

export function connectGitHub() {
  if (!S.session) { showToast('Please sign in first'); return; }

  // Check if already connected
  const token = ls('vc_github_token');
  if (token) {
    // Disconnect
    rmLs('vc_github_token');
    rmLs('vc_github_user');
    updateAccountStatus();
    showToast('GitHub disconnected');
    return;
  }

  // GitHub OAuth — redirect to GitHub authorization
  const clientId = window.__GITHUB_CLIENT_ID || '';
  if (!clientId) {
    // Fallback: manual token entry
    const pat = prompt('Paste your GitHub Personal Access Token (with repo scope):\n\nGet one at: github.com/settings/tokens');
    if (pat && pat.trim()) {
      setLs('vc_github_token', pat.trim());
      // Verify token
      fetch('https://api.github.com/user', {
        headers: { 'Authorization': 'token ' + pat.trim() }
      })
        .then(r => r.json())
        .then(data => {
          if (data.login) {
            setLs('vc_github_user', data.login);
            updateAccountStatus();
            showToast('✓ GitHub connected as ' + data.login);
          } else {
            rmLs('vc_github_token');
            showToast('⚠ Invalid token — check permissions');
          }
        })
        .catch(() => {
          rmLs('vc_github_token');
          showToast('⚠ Could not verify token');
        });
    }
    return;
  }

  // OAuth flow (when client ID is configured)
  const redirectUri = window.location.origin + '/api/github/callback';
  const scope = 'repo';
  const state = Math.random().toString(36).slice(2);
  sessionStorage.setItem('github_oauth_state', state);
  window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}`;
}

export function connectSupabase() {
  if (!S.session) { showToast('Please sign in first'); return; }

  // Check if already connected
  const url = ls('vc_supabase_url');
  if (url) {
    // Disconnect
    rmLs('vc_supabase_url');
    rmLs('vc_supabase_key');
    updateAccountStatus();
    showToast('Supabase disconnected');
    return;
  }

  // Manual entry (Supabase doesn't have a simple OAuth for project access)
  const supaUrl = prompt('Enter your Supabase Project URL:\n\n(e.g. https://abc123.supabase.co)\n\nFind it in: Supabase Dashboard → Settings → API');
  if (!supaUrl || !supaUrl.trim()) return;

  const supaKey = prompt('Enter your Supabase Service Role Key:\n\n(Find it in: Supabase Dashboard → Settings → API → service_role key)\n\n⚠️ Keep this secret — it has full access to your database.');
  if (!supaKey || !supaKey.trim()) return;

  // Verify connection
  fetch(supaUrl.trim() + '/rest/v1/', {
    headers: {
      'apikey': supaKey.trim(),
      'Authorization': 'Bearer ' + supaKey.trim(),
    }
  })
    .then(r => {
      if (r.ok || r.status === 200) {
        setLs('vc_supabase_url', supaUrl.trim());
        setLs('vc_supabase_key', supaKey.trim());
        updateAccountStatus();
        showToast('✓ Supabase connected');
      } else {
        showToast('⚠ Could not connect — check your URL and key');
      }
    })
    .catch(() => {
      // Even if fetch fails due to CORS, store it (Supabase REST may block browser requests)
      setLs('vc_supabase_url', supaUrl.trim());
      setLs('vc_supabase_key', supaKey.trim());
      updateAccountStatus();
      showToast('✓ Supabase credentials saved (could not verify — will test on first use)');
    });
}

export function updateAccountStatus() {
  const ghToken = ls('vc_github_token');
  const ghUser = ls('vc_github_user');
  const ghStatus = document.getElementById('github-status');
  const ghBtn = document.getElementById('github-connect-btn');

  if (ghStatus) {
    if (ghToken) {
      ghStatus.textContent = ghUser ? `Connected as ${ghUser}` : 'Connected';
      ghStatus.classList.add('connected');
    } else {
      ghStatus.textContent = 'Not connected';
      ghStatus.classList.remove('connected');
    }
  }
  if (ghBtn) {
    if (ghToken) {
      ghBtn.textContent = 'Disconnect';
      ghBtn.classList.add('disconnect');
    } else {
      ghBtn.textContent = 'Connect';
      ghBtn.classList.remove('disconnect');
    }
  }

  const sbUrl = ls('vc_supabase_url');
  const sbStatus = document.getElementById('supabase-status');
  const sbBtn = document.getElementById('supabase-connect-btn');

  if (sbStatus) {
    if (sbUrl) {
      // Show just the project ref from the URL
      const ref = sbUrl.replace('https://', '').replace('.supabase.co', '');
      sbStatus.textContent = `Connected (${ref})`;
      sbStatus.classList.add('connected');
    } else {
      sbStatus.textContent = 'Not connected';
      sbStatus.classList.remove('connected');
    }
  }
  if (sbBtn) {
    if (sbUrl) {
      sbBtn.textContent = 'Disconnect';
      sbBtn.classList.add('disconnect');
    } else {
      sbBtn.textContent = 'Connect';
      sbBtn.classList.remove('disconnect');
    }
  }
}
